import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { AuthService } from '../services/auth'
import { GoogleAuthService } from '../services/googleAuth'
import GoogleSignInButton from './GoogleSignInButton'

interface LoginDialogProps {
  isOpen: boolean
  onClose: () => void
}

const LoginDialog = ({ isOpen, onClose }: LoginDialogProps) => {
  const [loginMethod, setLoginMethod] = useState<'google' | 'password'>('password')
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  
  // Get Google Client ID from environment
  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setError('')
      // Set callback for Google auth success
      GoogleAuthService.setAuthSuccessCallback(() => {
        console.log('🟢 Google auth success callback triggered, closing dialog')
        onClose()
      })
      // Set callback for Google auth errors
      GoogleAuthService.setAuthErrorCallback((error: Error) => {
        console.log('🔴 Google auth error callback triggered:', error.message)
        setError(error.message)
      })
    } else {
      // Clear callbacks when dialog closes
      GoogleAuthService.setAuthSuccessCallback(null)
      GoogleAuthService.setAuthErrorCallback(null)
    }
  }, [isOpen, onClose])

  // Handle escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])


  const handleStudentLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsLoading(true)
      setError('')
      
      await AuthService.signInWithEmail(loginData.email, loginData.password)
      
      // Log successful login attempt
      await AuthService.logLoginAttempt(loginData.email, undefined, true)

      // Check the complete onboarding flow status
      const currentUser = await AuthService.getCurrentUser()
      const flowStatus = await AuthService.getOnboardingFlowStatus(currentUser || undefined)

      // Close dialog
      onClose()

      // Check if there's a returnUrl in the current URL
      const urlParams = new URLSearchParams(window.location.search)
      const returnUrl = urlParams.get('returnUrl')
      const defaultDestination = currentUser && ['teacher', 'admin', 'super_admin'].includes(currentUser.role)
        ? '/teacher'
        : '/videos'
      const destination = returnUrl ? decodeURIComponent(returnUrl) : defaultDestination

      // Route based on what the user needs to complete
      if (flowStatus.needsPasswordChange && flowStatus.needsOnboarding) {
        // Need both password change and onboarding
        const url = `${destination}${destination.includes('?') ? '&' : '?'}passwordchange=true&onboarding=true`
        window.location.href = url
      } else if (flowStatus.needsPasswordChange) {
        // Only need password change
        const url = `${destination}${destination.includes('?') ? '&' : '?'}passwordchange=true`
        window.location.href = url
      } else if (flowStatus.needsOnboarding) {
        // Only need onboarding
        const url = `${destination}${destination.includes('?') ? '&' : '?'}onboarding=true`
        window.location.href = url
      } else {
        // No special flow needed
        window.location.href = destination
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('Student login failed:', error)
      setError('ログインに失敗しました。メールアドレスとパスワードを確認してください。')
      
      // Log failed login attempt
      await AuthService.logLoginAttempt(loginData.email, undefined, false, errorMessage)
    } finally {
      setIsLoading(false)
    }
  }




  const handleGoogleError = (error: Error) => {
    console.error('🔴 Google authentication error:', error)
    setError('Googleログインに失敗しました')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Dialog Content */}
        <div className="px-8 pb-8">
          <div>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900">ログイン</h1>
                <p className="text-gray-600 mt-2">
                  アカウントにログインしてください
                </p>
              </div>

              {/* Login Method Toggle - Password first, then Google */}
              <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
                <button
                  type="button"
                  onClick={() => setLoginMethod('password')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    loginMethod === 'password'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  メールアドレス・パスワード
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('google')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    loginMethod === 'google'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Googleアカウント
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
                  <div className="text-red-800 text-sm">{error}</div>
                </div>
              )}

              {loginMethod === 'password' && (
                /* Email/Password Login */
                <form onSubmit={handleStudentLogin} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      メールアドレス
                    </label>
                    <input
                      type="email"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="student@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      パスワード
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="パスワードを入力"
                      required
                    />
                    <label className="flex items-center mt-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                        className="mr-2"
                      />
                      パスワードを表示
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isLoading ? 'ログイン中...' : 'ログイン'}
                  </button>

                  <div className="text-center">
                    <Link to="/forgot-password" className="text-sm text-blue-600 hover:text-blue-500">
                      パスワードを忘れた方はこちら
                    </Link>
                  </div>

                  <div className="mt-6 text-center text-sm text-gray-600">
                    学校経由で事前にアカウント登録が必要ですので、担当の先生にお問い合わせください。
                  </div>
                </form>
              )}

              {loginMethod === 'google' && (
                /* Google Login */
                <div className="space-y-4">
                  {googleClientId ? (
                    <>
                      <GoogleSignInButton
                        clientId={googleClientId}
                        text="signin_with"
                        theme="outline"
                        size="large"
                        className="w-full"
                        onError={handleGoogleError}
                      />
                    </>
                  ) : (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                      <p className="text-sm text-yellow-800">
                        Google Client IDが設定されていません。<br />
                        管理者にお問い合わせください。
                      </p>
                    </div>
                  )}

                  <div className="mt-6 text-center text-sm text-gray-600">
                    学校経由で事前にアカウント登録が必要ですので、担当の先生にお問い合わせください。
                  </div>
                </div>
              )}
            </div>
        </div>
      </div>
    </div>
  )
}

export default LoginDialog
